import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const layers = [
  {
    level: '底层',
    title: '单课评价',
    items: ['综合总分', '维度分', '分档结果', '证据链', '改进建议'],
    color: 'primary' as const,
    icon: 'ri-file-list-3-line',
  },
  {
    level: '中层',
    title: '四类使用对象',
    items: ['教师', '学生', '专家', '管理者'],
    color: 'accent' as const,
    icon: 'ri-user-shared-line',
  },
  {
    level: '上层',
    title: '跟踪结果',
    items: ['教师成长', '教研改进', '学科质量', '区域跟踪'],
    color: 'primary' as const,
    icon: 'ri-government-line',
  },
];

export default function P60FromAI2Empower({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* 明显可见的三层背景光效 */}
      <div ref={orb1Ref} className="absolute top-16 right-20 w-[400px] h-[400px] rounded-full bg-primary-200/30 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-24 left-12 w-[420px] h-[420px] rounded-full bg-accent-200/25 blur-[110px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[350px] rounded-full bg-secondary-200/20 blur-[150px] pointer-events-none animate-orb-float-3" />

      <div className="relative z-10 w-full max-w-5xl mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-24">
        {/* Title */}
        <div ref={titleRef} className="text-center mb-8 md:mb-10">
          <span className={`inline-flex items-center gap-2 text-sm font-body font-semibold text-gold-600 uppercase tracking-[0.15em] mb-3 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />第四部分 · 应用服务与质量跟踪
          </span>
          <h2 className={`font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-3 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent">从AI评课能力到教学质量跟踪</span>
          </h2>
          <p className={`text-base md:text-lg text-foreground-950 font-body max-w-2xl mx-auto leading-snug transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            评价不是终点，评价结果进入教学行动和跟踪流程才产生持续价值
          </p>
        </div>

        {/* Three Column Layout */}
        <div className={`max-w-5xl mx-auto w-full mb-8 md:mb-10 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-8 items-stretch">
            {layers.map((layer, li) => (
              <div key={layer.level} className="relative flex flex-col">
                {/* Arrow between columns (desktop only) */}
                {li > 0 && (
                  <div className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 items-center">
                    <i className="ri-arrow-right-line text-base md:text-lg text-foreground-950" />
                  </div>
                )}

                <div className="flex-1 p-6 md:p-8 glass-liquid rounded-xl hover:-translate-y-1 hover:border-gold-300/50 transition-all duration-500 text-center">
                  <div className="flex items-center justify-center gap-2 mb-3 md:mb-4">
                    <span className="text-sm font-mono font-bold text-gold-700 bg-gold-50/80 px-3 py-1 rounded-full border border-gold-200/40">{layer.level}</span>
                  </div>
                  <div className="flex items-center justify-center gap-2 mb-4 md:mb-5">
                    <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg ${layer.color === 'accent' ? 'glass-gold border-gold-200/40' : 'glass-blue border-primary-200/40'} flex items-center justify-center`}>
                      <i className={`${layer.icon} text-base md:text-lg ${layer.color === 'accent' ? 'text-gold-600' : 'text-primary-600'}`} />
                    </div>
                    <span className={`font-heading text-base md:text-lg font-bold ${layer.color === 'accent' ? 'text-gold-700' : 'text-primary-700'}`}>{layer.title}</span>
                  </div>
                  <div className="flex flex-wrap items-center justify-center gap-2 md:gap-2.5">
                    {layer.items.map((item) => (
                      <span
                        key={item}
                        className={`inline-block px-3 py-1.5 rounded-full text-sm md:text-base font-body font-semibold ${
                          layer.color === 'accent'
                            ? 'glass-gold border-gold-200/40 text-gold-700'
                            : 'glass-blue border-primary-200/40 text-primary-700'
                        }`}
                        style={{ transitionDelay: `${300 + li * 150}ms` }}
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Arrow below on mobile */}
                {li < layers.length - 1 && (
                  <div className="flex md:hidden justify-center py-2">
                    <i className="ri-arrow-down-line text-base text-foreground-950" />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Data chain connector */}
          <div className={`flex justify-center mt-8 transition-all duration-1000 delay-600 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
            <div className="flex items-center gap-2 px-5 py-3 glass-liquid rounded-full border border-gold-200/40 hover:border-gold-300/60 transition-all duration-300">
              <i className="ri-database-2-line text-sm md:text-base text-gold-600" />
              <span className="text-sm md:text-base font-body text-foreground-950">课堂事实不断汇聚、应用层级逐步提升</span>
              <i className="ri-arrow-up-line text-sm md:text-base text-gold-600" />
            </div>
          </div>
        </div>

        {/* Bottom Text */}
        <div className={`max-w-3xl mx-auto transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-6 md:p-8 glass-liquid rounded-xl hover:border-primary-300/50 transition-all duration-300">
            <p className="text-base md:text-lg text-foreground-950 font-body leading-relaxed text-center">
              一份AI评课报告不应停留在系统中，也不能只作为一次性分数。它首先应当帮助教师复盘课堂，帮助学生找到重点，帮助专家快速进入关键证据，也帮助管理者从大量课程中看清整体结构。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}